import type { TokenResponse } from '@bia-auction/shared-schema';
import { tokenResponseSchema } from '@bia-auction/shared-schema';
import { API_BASE } from './config';

/**
 * Token store (ADR 0019): the **access token lives in memory only**; the **refresh token is
 * persisted** in localStorage so a reload stays logged in. Refresh is single-flight (concurrent
 * 401s share one refresh). No cookies — every request sends `Authorization: Bearer`.
 *
 * **This file is deliberately identical in `web-admin` and `web-bidder` but for `REFRESH_KEY`.**
 * Duplicated concurrency logic is the kind that drifts, so a change here belongs in both copies;
 * the test files are duplicated for the same reason. (Extracting it into a package would mean a
 * shared module that knows about `localStorage`, which neither app's build wants.)
 *
 * ## Cross-context refresh (M19, audit O-04)
 *
 * `/auth/refresh` rotates with a compare-and-set on `revoked_at` and **deliberately rejects the
 * loser** of a concurrent rotation inside its reuse-grace window (`routes/auth.ts`) — that is
 * what tells a stolen token from a double-submit. The single-flight promise below covers only
 * *this* JS context, so two of them (a second tab, or a page reloading over an in-flight
 * refresh) both send a refresh and the loser gets a 401 for a session that is perfectly alive.
 *
 * Clearing on that 401 is what dropped an operator to the login screen mid-navigation. So a
 * rejection is now checked against the stored token first: if another context has written a
 * newer one, this was the losing half of a race and the retry uses the winner's token.
 */

const REFRESH_KEY = 'bia.bidder.refresh';
let accessToken: string | null = null;
let accessExpiry = 0; // epoch-ms
// Bumped whenever the session is torn down; an in-flight refresh captures it and refuses to
// commit its (stale) rotated tokens if the session changed (logout → re-login as another user).
let sessionGen = 0;

const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}
/** Subscribe to token changes (for `useSyncExternalStore`). */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAccessToken(): string | null {
  return accessToken;
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
/** A persisted refresh token means we can (re)establish a session. */
export function isAuthenticated(): boolean {
  return getRefreshToken() !== null;
}

export function setTokens(t: TokenResponse): void {
  accessToken = t.accessToken;
  accessExpiry = Date.now() + t.expiresIn * 1000;
  localStorage.setItem(REFRESH_KEY, t.refreshToken);
  emit();
}
export function clearTokens(): void {
  sessionGen++;
  accessToken = null;
  accessExpiry = 0;
  localStorage.removeItem(REFRESH_KEY);
  emit();
}

// Another tab writing a token means our own view of the session changed under us — re-render,
// so a tab whose session ended elsewhere stops showing a signed-in shell it can no longer use.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === REFRESH_KEY) emit();
  });
}

/**
 * How long to wait for another context's rotation to land before concluding that a rejected
 * refresh really was a dead session. Comfortably inside the server's reuse-grace window, and
 * short enough that a genuinely expired session still reaches the login screen promptly.
 */
const ROTATION_WAIT_MS = 1_500;

/**
 * How many times to re-try against a newly published token. More than one because with three
 * or more contexts the first retry can itself lose the race; bounded so a session that really
 * is dead still reaches the login screen without a long stall.
 */
const MAX_ROTATION_RETRIES = 3;

/** Poll the stored token until it differs from `sent`, or the wait elapses. */
async function awaitRotation(sent: string): Promise<string | null> {
  const deadline = Date.now() + ROTATION_WAIT_MS;
  const rotated = () => {
    const current = getRefreshToken();
    return current && current !== sent ? current : null;
  };
  // Polled rather than driven by the `storage` event alone: that event does not fire in the tab
  // that made the write, so the winner-and-loser pair inside ONE tab (a page load racing an
  // in-flight refresh) would never hear it.
  while (Date.now() < deadline) {
    const current = rotated();
    if (current) return current;
    await new Promise((r) => setTimeout(r, 100));
  }
  // One last read *after* the loop: a token published between the final poll and the deadline
  // would otherwise be missed, and the caller would clear a session the winner had just
  // renewed — the same failure this function exists to prevent, one tick later.
  return rotated();
}

let refreshing: Promise<string | null> | null = null;

/** Rotate the refresh token → new access token. Single-flight; clears the session on failure. */
export function refreshAccess(): Promise<string | null> {
  if (refreshing) return refreshing;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(null);
  const gen = sessionGen; // capture the session this refresh belongs to
  refreshing = (async () => {
    try {
      const post = (token: string) =>
        fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ refreshToken: token }),
        });

      // Each pass sends the newest token we know of. A rejection is only conclusive once no
      // other context has published a newer one — with three or more tabs the first retry can
      // lose the race too, and clearing then would end a session that is perfectly alive.
      // Bounded, so a genuinely dead session still reaches the login screen promptly.
      let attempt = refreshToken;
      let res = await post(attempt);
      if (gen !== sessionGen) return null;

      for (let i = 0; i < MAX_ROTATION_RETRIES && (res.status === 401 || res.status === 403); i++) {
        const rotated = await awaitRotation(attempt);
        if (gen !== sessionGen) return null;
        if (!rotated) break; // nobody rotated — the rejection stands
        attempt = rotated;
        res = await post(attempt);
        if (gen !== sessionGen) return null;
      }

      if (res.status === 401 || res.status === 403) {
        // Only conclusive if no other context has published something newer. With enough tabs
        // the loop above can exhaust its cap while a further winner has just landed, and
        // clearing then would end a live session — the failure this whole path exists to
        // prevent, arriving via the bound instead of via the first rejection.
        if (getRefreshToken() === attempt) clearTokens();
        return null;
      }
      // A transient 429/5xx keeps the persisted token so a later call can retry instead of
      // forcing login.
      if (!res.ok) return null;

      const data = tokenResponseSchema.parse(await res.json());
      if (gen !== sessionGen) return null;
      setTokens(data);
      return data.accessToken;
    } catch {
      // A transport error (offline / DNS / TLS) is transient — keep the persisted refresh token
      // so a later call retries, rather than forcing the operator back to login.
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** A valid (non-expired) access token, refreshing proactively when it is close to expiry. */
export async function ensureAccess(): Promise<string | null> {
  if (accessToken && Date.now() < accessExpiry - 10_000) return accessToken;
  return refreshAccess();
}
